import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';
import { rawTagSchema } from './schemas';

/**
 * Hardware uplinks. Every route here is **DERIVED scope**, declared in
 * `PROJECT_LESS_BY_DESIGN`: the caller is a device or a gateway, it names a
 * `uuid` and never a tenant, and the server resolves the project from the stored
 * `originProjectId` on the tag plus the anchor -> project mapping. Forcing
 * `/projects/:projectId` onto them would be a claim nobody supplies and nothing
 * reads.
 *
 * `{ root: true }` because the paths predate the contract-name convention and the
 * gateways calling them live outside this repository. `getAllTags` and
 * `deleteBeforeDate` have left: the first is project data and moved to
 * `mergedTags.contract.ts`, the second is deleted.
 */
export const tagsContract = defineContract(
	'tags',
	{
		updateTags: route({
			method: 'POST',
			path: '/update',
			body: rawTagSchema,
			response: z.string(),
			operationId: 'updateTags',
			tags: ['Tags'],
			summary: 'Ingest a raw tag uplink (derived scope)',
		}),
		/**
		 * Called by HardwareCommunication's `abeeway` and `lansitech` uplink
		 * handlers, which are themselves derived-scope routes. The `:uuid` is a
		 * device id; the project comes from the tag row the write resolves.
		 */
		updateBattery: route({
			method: 'PUT',
			path: '/updateBattery/:uuid',
			params: z.object({ uuid: requiredStringSchema }),
			body: z.object({ batteryLevel: z.string() }),
			response: z.string(),
			operationId: 'updateBattery',
			tags: ['Tags'],
			summary: 'Update a tag battery level from a device uplink (derived scope)',
		}),
		sosAlert: route({
			method: 'POST',
			path: '/sosAlert',
			body: rawTagSchema,
			response: z.string(),
			operationId: 'sosAlert',
			tags: ['Tags'],
			summary: 'Handle an SOS alert from a tag (derived scope)',
		}),
	},
	{ root: true }
);
